
import React from 'react';
import { Task, TaskStatus, useTasks } from '@/context/TaskContext';
import { Droppable, Draggable } from 'react-beautiful-dnd';
import SwipeableTaskCard from '@/components/SwipeableTaskCard';
import { toast } from 'sonner';
import { PlusIcon } from 'lucide-react';

interface TaskColumnProps {
  title: string;
  droppableId: string;
  tasks: Task[];
  colorClass: string;
  onTaskSelect: (id: string) => void;
  activeTaskId: string | undefined;
  onAddNewTask?: () => void;
  emptyStateText?: string;
  hideHeader?: boolean;
  isDragInProgress?: boolean;
}

const TaskColumn: React.FC<TaskColumnProps> = ({
  title,
  droppableId,
  tasks,
  colorClass,
  onTaskSelect,
  activeTaskId,
  onAddNewTask,
  emptyStateText,
  hideHeader = false,
  isDragInProgress = false
}) => {
  const { deleteTask, updateTaskStatus } = useTasks();
  
  const handleDeleteTask = (taskId: string) => {
    deleteTask(taskId);
    toast.success('Task deleted');
  };
  
  const handleMarkDone = (taskId: string) => {
    updateTaskStatus(taskId, 'done');
    toast.success('Task marked as done');
  };

  // Text to show in empty state button
  const buttonText = emptyStateText || `Add a ${title.toLowerCase()} task`;
  
  return (
    <div className="kanban-column h-full overflow-visible">
      {!hideHeader && (
        <div className="flex items-center mb-4">
          <div className={`w-3 h-3 ${colorClass} rounded-full mr-2`}></div>
          <h3 className="font-semibold text-gray-900 dark:text-white">{title}</h3>
          <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">
            ({tasks.length})
          </span>
        </div>
      )}
      
      <Droppable droppableId={droppableId}>
        {(provided, snapshot) => (
          <div 
            {...provided.droppableProps}
            ref={provided.innerRef}
            className={`
              min-h-[140px] h-full rounded-lg transition-colors duration-200 relative overflow-visible
              ${snapshot.isDraggingOver 
                ? 'bg-gray-100 dark:bg-gray-800 border-2 border-dashed border-gray-300 dark:border-gray-600' 
                : 'bg-transparent'
              }
            `}
            style={{ paddingBottom: '8px' }}
          >
            {tasks.length === 0 ? (
              <div className="border border-dashed border-gray-300 dark:border-gray-700 rounded-lg p-4 sm:p-6 flex items-center justify-center min-h-[100px] sm:min-h-[120px] bg-gray-50 dark:bg-gray-900/20">
                <button 
                  onClick={onAddNewTask}
                  disabled={isDragInProgress}
                  className="flex items-center justify-center gap-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors text-sm font-medium disabled:opacity-50"
                >
                  <PlusIcon size={16} />
                  <span>{buttonText}</span>
                </button>
              </div>
            ) : (
              <div className="space-y-2 sm:space-y-3 overflow-visible">
                {tasks.map((task, index) => (
                  <Draggable 
                    key={task.id} 
                    draggableId={task.id} 
                    index={index}
                    isDragDisabled={isDragInProgress}
                  >
                    {(provided, snapshot) => {
                      // Safely access style properties with type checking
                      const style = provided.draggableProps.style;
                      const isDraggingStyle = style && 'position' in style && style.position === 'fixed';
                      
                      return (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          className={`
                            transition-all duration-200 ease-out relative overflow-visible
                            ${snapshot.isDragging 
                              ? 'z-[9999] rotate-2 scale-105 shadow-2xl opacity-95' 
                              : 'z-auto rotate-0 scale-100 shadow-sm opacity-100'
                            }
                          `}
                          style={{
                            ...style,
                            minHeight: '100px',
                            // Ensure dragged items have proper positioning
                            position: snapshot.isDragging ? 'fixed' : 'relative',
                            top: snapshot.isDragging && isDraggingStyle ? style?.top : 'auto',
                            left: snapshot.isDragging && isDraggingStyle ? style?.left : 'auto',
                            transform: snapshot.isDragging 
                              ? `${style?.transform || ''} rotate(2deg)` 
                              : style?.transform,
                            pointerEvents: snapshot.isDragging ? 'none' : 'auto',
                            zIndex: snapshot.isDragging ? 9999 : 'auto'
                          }}
                        >
                          <SwipeableTaskCard 
                            task={task} 
                            onSelect={onTaskSelect}
                            isActive={task.id === activeTaskId}
                            compact={true}
                            onDelete={handleDeleteTask}
                            onMarkDone={handleMarkDone}
                            isDragging={snapshot.isDragging}
                          />
                        </div>
                      );
                    }}
                  </Draggable>
                ))}
                {/* Add some padding at the bottom for drop zone */}
                <div className="h-4" />
              </div>
            )}
            {provided.placeholder}
            
            {/* Drop indicator at the bottom when dragging */}
            {snapshot.isDraggingOver && (
              <div className="absolute bottom-2 left-2 right-2 h-2 bg-blue-200 dark:bg-blue-800 rounded-full opacity-50 animate-pulse" />
            )}
          </div>
        )}
      </Droppable>
    </div>
  );
};

export default TaskColumn;
